import Item from "@components/Layouts/LeftNavigationTabs/Item";
import calculateTabWrapperHeight from "@components/Layouts/LeftNavigationTabs/logic/calculateTabWrapperHeight";
import type { ArticleProviderType } from "@ext/articleProvider/logic/ArticleProvider";
import type { ProviderItemProps } from "@ext/articleProvider/models/types";
import t from "@ext/localization/locale/translate";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import BaseRightExtensions from "./BaseRightExtensions";

interface ItemListProps<T = ProviderItemProps> {
	show: boolean;
	tabWrapperRef: RefObject<HTMLDivElement>;
	selectedItemId: string | string[];
	items: T[];
	noItemsText: string;
	providerType?: ArticleProviderType;
	confirmDeleteText?: string;
	onItemClick: (id: string, target: HTMLElement) => void;
	onDelete?: (id: string) => void;
	setContentHeight: (height: number) => void;
	onMarkdownChange?: (id: string, markdown: string) => void;
	preDelete?: (id: string) => Promise<boolean>;
	rightActions?: (id: string) => ReactNode;
}

type ItemRowProps = {
	id: string;
	title: string;
	isSelected: boolean;
	onItemClick: (id: string, target: HTMLElement) => void;
	rightActions: (onOpenChange: (open: boolean) => void) => ReactNode;
};

const ItemRow = ({ id, title, isSelected, onItemClick, rightActions }: ItemRowProps) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	return (
		<Item
			id={id}
			isMenuOpen={isMenuOpen}
			isSelected={isSelected}
			onItemClick={onItemClick}
			rightActions={rightActions(setIsMenuOpen) as JSX.Element}
			rightActionsWidth="0.85em"
			title={title}
		/>
	);
};

const ItemList = <T extends ProviderItemProps>(props: ItemListProps<T>) => {
	const {
		show,
		tabWrapperRef,
		setContentHeight,
		providerType,
		items,
		onDelete,
		onMarkdownChange,
		selectedItemId: selectedId,
		rightActions,
		noItemsText,
		onItemClick,
		preDelete,
		confirmDeleteText,
	} = props;
	const ref = useRef<HTMLDivElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	useEffect(() => {
		if (!ref.current || !tabWrapperRef.current || !show) return;
		const mainElement = tabWrapperRef.current;
		setContentHeight(calculateTabWrapperHeight(mainElement));
	}, [show, items.length]);

	const itemList = useMemo(() => {
		return items.map((item) => (
			<ItemRow
				id={item.id}
				isSelected={Array.isArray(selectedId) ? selectedId.includes(item.id) : selectedId === item.id}
				key={item.id}
				onItemClick={onItemClick}
				rightActions={(onOpenChange) =>
					onMarkdownChange &&
					onDelete && (
						<BaseRightExtensions
							confirmDeleteText={confirmDeleteText}
							id={item.id}
							items={rightActions}
							onDelete={onDelete}
							onMarkdownChange={onMarkdownChange}
							onOpenChange={onOpenChange}
							preDelete={preDelete}
							providerType={providerType}
						/>
					)
				}
				title={item.title.length ? item.title : t("article.no-name")}
			/>
		));
	}, [
		items,
		selectedId,
		onItemClick,
		onDelete,
		onMarkdownChange,
		preDelete,
		providerType,
		rightActions,
		confirmDeleteText,
	]);

	if (!show) return;

	return (
		<ScrollShadowContainer className="max-h-[40vh] overflow-x-hidden" ref={ref}>
			{items.length ? itemList : <div style={{ paddingLeft: "1rem", paddingRight: "1rem" }}>{noItemsText}</div>}
		</ScrollShadowContainer>
	);
};

export default ItemList;
